import { useState } from 'react';
import clsx from 'clsx';
import { RootStateOrAny, useSelector } from 'react-redux';
import type { EmployerInterviewButtonProps } from './EmployerInterviewButton.types';
import { ButtonState } from './EmployerInterviewButton.types';
import { Button } from 'components/Button';
import { postHogCapture } from 'components/analytics/PostHog';
import { SendInviteModal } from './SendInviteModal';
import { ButtonStateToData } from './EmployerInterviewButton.constants';
import isAfter from 'date-fns/isAfter';
import format from 'date-fns/format';
import { StatusDetailWrapper } from './StatusDetailWrapper';
import { InterviewSentModal } from 'features/InterviewSentModal';
import { CompleteInterviewRequirementsModal } from 'features/CompleteInterviewRequirementsModal';

const eventCapture = postHogCapture(false);

/** This the button for employers to create interview educators/applicants */
export const EmployerInterviewButton = (props: EmployerInterviewButtonProps) => {
  const {
    educatorId,
    posthog,
    disabled,
    onInviteSend,
    userProfileMetaData,
    className,
    onlyButton = false,
    interviewEventTypes,
    availabilitySlots,
  } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [interviewSentModalOpen, setInterviewSentModalOpen] = useState(false);
  const [completeInterviewRequirementsModalOpen, setCompleteInterviewRequirementsModalOpen] =
    useState(false);

  const authenticated = useSelector((state: RootStateOrAny) => state.account.authenticated);

  if (!authenticated) return null;

  const hasEventTypes = !!interviewEventTypes?.length;
  const hasAvailability = !!availabilitySlots?.length;

  let displayDate = null;
  const getStatus = () => {
    const interview = userProfileMetaData?.interview();
    const invitationDateString = userProfileMetaData?.conversation?.lastInterviewMessage?.createdAt;

    if (!invitationDateString) return ButtonState.DEFAULT;

    const lastInvitationDate = new Date(invitationDateString);

    if (interview?.time) {
      const interviewDate = new Date(interview.time);
      const interviewStatus = interview.status;
      const interviewCanceled = interviewStatus.toLowerCase() === 'canceled';

      if (interviewCanceled) {
        displayDate = null;
        return ButtonState.INTERVIEW_CANCELED;
      }

      const currentDate = new Date();
      displayDate = format(interviewDate, 'MM/dd/yyyy');
      return isAfter(currentDate, interviewDate)
        ? ButtonState.INTERVIEW_ATTENDED
        : ButtonState.INTERVIEW_SCHEDULED;
    }

    displayDate = format(lastInvitationDate, 'MM/dd/yyyy');
    return ButtonState.INVITATION_SENT;
  };

  const status = getStatus();
  const isCanceled = status === ButtonState.INTERVIEW_CANCELED;

  const { button, message } = ButtonStateToData.get(status);
  const { icon: ButtonIcon, label, variant, btnClassName } = button;
  const { icon: MessageIcon, prefix, preventInvite, messageClassName } = message || {};

  return (
    <div className={clsx('text-center', className)}>
      {!preventInvite && (
        <>
          <SendInviteModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
            }}
            onInviteSend={() => {
              onInviteSend?.();
              setModalOpen(false);
              setInterviewSentModalOpen(true);
            }}
            educatorId={educatorId}
            interviewEventTypes={interviewEventTypes}
          />
          <CompleteInterviewRequirementsModal
            open={completeInterviewRequirementsModalOpen}
            onClose={() => {
              setCompleteInterviewRequirementsModalOpen(false);
            }}
            hasAvailability={hasAvailability}
          />
        </>
      )}
      <InterviewSentModal
        open={interviewSentModalOpen && !modalOpen}
        onClose={() => {
          setInterviewSentModalOpen(false);
        }}
      />
      <Button
        className={clsx('gap-2.5 w-full max-sm:!h-[32px]', btnClassName)}
        aria-disabled={preventInvite}
        variant={variant}
        fullWidth
        onClick={
          !preventInvite
            ? () => {
                if (!hasEventTypes || !hasAvailability) {
                  setCompleteInterviewRequirementsModalOpen(true);
                } else {
                  setModalOpen(true);
                }
                if (posthog?.eventName) eventCapture(posthog.eventName, posthog.properties);
              }
            : null
        }
        disabled={disabled}
      >
        <ButtonIcon className="w-6 h-6 shrink-0" />
        <span>{label}</span>
      </Button>
      {((message && prefix && !onlyButton) || null) && (
        <StatusDetailWrapper isCanceled={isCanceled} className={messageClassName}>
          <MessageIcon className="w-4 h-4" />
          {prefix} {displayDate}
        </StatusDetailWrapper>
      )}
    </div>
  );
};

EmployerInterviewButton.displayName = 'EmployerInterviewButton';
